
import React, { useState, useEffect, useRef } from 'react';

interface LandingPageProps {
  onStart: (language: string) => void;
}

const translations: Record<string, any> = {
  en: {
    tagline1: "Move smarter.",
    tagline2: "Settle faster.",
    selectLang: "SELECT YOUR NATIVE LANGUAGE",
    start: "Start"
  },
  zh: {
    tagline1: "更聪明地搬迁。",
    tagline2: "更快速地安顿。",
    selectLang: "选择你的母语",
    start: "开始"
  },
  es: {
    tagline1: "Elige una ciudad.",
    tagline2: "Instálate.",
    selectLang: "SELECCIONA TU IDIOMA NATIVO",
    start: "Comenzar"
  },
  fr: {
    tagline1: "Choisissez une ville.",
    tagline2: "Installez-vous.",
    selectLang: "SÉLECTIONNEZ VOTRE LANGUE MATERNELLE",
    start: "Démarrer"
  },
  ar: {
    tagline1: "اختر مدينة.",
    tagline2: "استقر.",
    selectLang: "اختر لغتك الأم",
    start: "ابدأ"
  },
  ja: {
    tagline1: "都市を選ぶ。",
    tagline2: "生活を始める。",
    selectLang: "母国语を選択してください",
    start: "開始"
  },
  ko: {
    tagline1: "도시를 선택하세요.",
    tagline2: "정착하세요.",
    selectLang: "모국어를 선택하세요",
    start: "시작"
  },
};

const languages = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
];

const LandingPage: React.FC<LandingPageProps> = ({ onStart }) => {
  const [selectedLang, setSelectedLang] = useState('en');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = translations[selectedLang] || translations.en;
  const isRTL = selectedLang === 'ar';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative min-h-screen w-full overflow-hidden bg-background-light selection:bg-primary/30 selection:text-slate-dark text-slate-dark font-display ${isRTL ? 'rtl' : 'ltr'}`}>
      <style>{`
        @keyframes aurora-breath {
          0%, 100% { transform: translate(0, 0) rotate(-15deg) scale(1); opacity: 0.8; }
          50% { transform: translate(-1%, 1%) rotate(-12deg) scale(1.02); opacity: 0.6; }
        }
        @keyframes grid-drift {
          0% { background-position: 0 0; }
          100% { background-position: 40px 40px; }
        }
        .animate-aurora { animation: aurora-breath 20s ease-in-out infinite; }
        .animate-grid { animation: grid-drift 60s linear infinite; }
      `}</style>

      {/* Background Layer */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-background-light">
          <div className="absolute inset-0 origin-center animate-grid" style={{ transform: 'rotate(-15deg) scale(1.8)' }}>
            <div className="absolute inset-0 grid-pattern opacity-30"></div>
          </div>
          <div className="aurora-blur animate-aurora"></div>
        </div>
      </div>

      {/* Content Layer */}
      <div className="relative z-10 flex flex-col min-h-screen w-full">
        <div className="flex-1 flex flex-col max-w-xl mx-auto w-full px-6 md:px-12">
          
          <header className="pt-12 md:pt-20 pb-16 flex items-center justify-between w-full">
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className="w-12 h-12 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-[32px] drop-shadow-sm font-bold">flight_takeoff</span>
              </div>
              <h2 className="text-slate-dark tracking-[-0.03em] text-[28px] font-[900] leading-none">Settlo</h2>
            </div>
          </header>

          <main className="flex-1 flex flex-col justify-center pb-24">
            <section className={`mb-20 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h1 className={`flex flex-col text-[62px] md:text-[84px] font-[900] tracking-[-0.06em] leading-[0.95] ${isRTL ? 'items-end' : 'items-start'}`}>
                <span className="text-slate-dark mb-2">{t.tagline1}</span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0066FF] to-[#0044CC] drop-shadow-[0_2px_10px_rgba(0,102,255,0.1)]">
                  {t.tagline2}
                </span>
              </h1>
            </section>

            <section className="space-y-12">
              <div className="relative w-full" ref={dropdownRef}>
                <label className={`block text-slate-soft/40 text-[11px] font-[900] uppercase tracking-[0.3em] mb-5 ${isRTL ? 'text-right pr-1' : 'text-left pl-1'}`}>
                  {t.selectLang}
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`group w-full flex items-center justify-between bg-white/70 backdrop-blur-md text-slate-dark text-[18px] font-bold py-6 px-8 rounded-stripe border border-[#E6E6E8] shadow-stripe-input hover:border-primary transition-all duration-300 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <span className="tracking-tight">{languages.find(l => l.code === selectedLang)?.label}</span>
                    <span className={`material-symbols-outlined text-slate-soft/60 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-4 py-3 bg-white/95 backdrop-blur-xl border border-[#E6E6E8] rounded-stripe shadow-2xl z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200 max-h-[40vh] overflow-y-auto no-scrollbar">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setSelectedLang(lang.code);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full px-8 py-5 text-[17px] font-bold transition-all flex items-center justify-between hover:bg-primary/5 ${
                            selectedLang === lang.code ? 'text-primary bg-primary/[0.03]' : 'text-slate-soft hover:text-slate-dark'
                          } ${isRTL ? 'flex-row-reverse text-right' : 'flex-row text-left'}`}
                        >
                          <span>{lang.label}</span>
                          {selectedLang === lang.code && <span className="material-symbols-outlined text-[24px]">check</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button 
                onClick={() => onStart(selectedLang)}
                className="group w-full relative flex items-center justify-center gap-4 bg-primary hover:bg-primary-dark text-white font-[900] text-[22px] py-6 rounded-stripe shadow-[0_12px_40px_rgba(0,102,255,0.35)] transition-all duration-500 transform active:scale-[0.98] active:shadow-none overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="tracking-tight">{t.start}</span>
                <span className={`material-symbols-outlined text-[28px] transition-all duration-300 ${isRTL ? 'group-hover:-translate-x-3 rotate-180' : 'group-hover:translate-x-3'}`}>
                  arrow_forward
                </span>
              </button>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
